export interface FecProgressSnapshot {
	sourceSymbols: number;
	repairSymbols: number;
	blocksComplete: number;
	blocksRecovered: number;
	mediaFrames?: number;
}

export interface StallDetectorConfig {
	windowMs: number;
	onStarvation(): void;
	onRecovery(): void;
	now?: () => number;
}

export interface PlayerStallSnapshot {
	liveQ?: number | null;
	decodeCallsDelta?: number | null;
	fps?: number | null;
}

export interface PlayerStallClassification {
	verdict: "DECODE-BOUND" | "PIPELINE-STARVED" | "STARVED-OR-SLOW" | "INDETERMINATE";
	reason: string;
}

export function classifyStall({
	liveQ = null,
	decodeCallsDelta = null,
	fps = null,
}: PlayerStallSnapshot = {}): PlayerStallClassification {
	if (liveQ != null && liveQ >= 20) {
		return {
			verdict: "DECODE-BOUND",
			reason: `decode queue backlogged now (liveQ=${liveQ})`,
		};
	}
	if (liveQ != null && liveQ <= 2 && decodeCallsDelta != null && decodeCallsDelta <= 1) {
		return {
			verdict: "PIPELINE-STARVED",
			reason: `queue empty (liveQ=${liveQ}) and decode flatlined (Δcalls=${decodeCallsDelta}) — nothing reaching the decoder; suspect delivery/FEC/failback, not decode perf`,
		};
	}
	if (fps != null && fps < 45) {
		return {
			verdict: "STARVED-OR-SLOW",
			reason: `low decode rate (fps=${fps}) without a queue/flatline signal to disambiguate`,
		};
	}
	return { verdict: "INDETERMINATE", reason: "no signal supports a verdict" };
}

export class TransportStallDetector {
	#last: FecProgressSnapshot | null = null;
	#lastSampleAt = 0;
	#starvedSince: number | null = null;
	#latched = false;
	readonly #now: () => number;

	constructor(private readonly config: StallDetectorConfig) {
		if (config.windowMs <= 0) throw new Error("Stall window must be positive and catalog-derived");
		this.#now = config.now ?? Date.now;
	}

	get isStarved(): boolean { return this.#latched; }

	update(snapshot: FecProgressSnapshot): void {
		const previous = this.#last;
		const previousAt = this.#lastSampleAt;
		const now = this.#now();
		this.#last = { ...snapshot };
		this.#lastSampleAt = now;
		if (!previous) return;

		const blockDelta = snapshot.blocksComplete - previous.blocksComplete +
			snapshot.blocksRecovered - previous.blocksRecovered;
		const mediaDelta = (snapshot.mediaFrames ?? 0) - (previous.mediaFrames ?? 0);

		if (blockDelta <= 0 && mediaDelta <= 0) {
			this.#starvedSince ??= previousAt;
			if (!this.#latched && now - this.#starvedSince >= this.config.windowMs) {
				this.#latched = true;
				this.config.onStarvation();
			}
			return;
		}

		this.#starvedSince = null;
		if (this.#latched) {
			this.#latched = false;
			this.config.onRecovery();
		}
	}

	reset(): void {
		this.#last = null;
		this.#starvedSince = null;
		this.#latched = false;
	}
}
